import React, { useState } from "react"

import {
	Grid,
	Typography,
	Link
} from "@material-ui/core"
import { Alert } from "@material-ui/lab"

import {
	ActionDialog,
	Notification
} from "@/components"
import SelectInput from "@/pages/Admin/Flow/FlowConstructor/FlowEditor/components/SelectInput"

import {
	ConstructionResources,
	ShortChatBotTrigger
} from "@/protocols/chatBotConstructor"

import useDidMount from "@/hooks/useDidMount"

import { BoardStageWithoutCards } from "@/services/BoardStage"
import { BoardSummary } from "@/services/BoardService"
import ErrorHandlerService from "@/services/ErrorHandler"

type ClientAddedToFunnelEditorDialogProps = {
	saveText?: string
	onSave?: (board: BoardStageWithoutCards | null) => void
	onClose?: () => void
	onLoadBoardStages: () => Promise<ConstructionResources["boards"] | null>
	trigger: ShortChatBotTrigger
}

const ClientAddedToFunnelEditorDialog: React.FC<ClientAddedToFunnelEditorDialogProps> = (props) => {
	const {
		saveText,
		onClose,
		onSave,
		onLoadBoardStages,
		trigger,
		children
	} = props

	const [openDialog, setOpenDialog] = useState(false)
	const [saving, setSaving] = useState(false)
	const [selectedBoardStage, setSelectedBoardStage] = useState<BoardStageWithoutCards | null>(null)
	const [boardOptions, setBoardOptions] = useState<BoardSummary[]>([])

	const handleOpenDialog = async () => {
		setOpenDialog(true)
	}

	const handleCloseDialog = () => {
		setOpenDialog(false)
	}

	const handleClose = () => {
		handleCloseDialog()

		onClose?.()
	}

	const handleChange = (boardStage: BoardStageWithoutCards | null) => {
		setSelectedBoardStage(boardStage)
	}

	const handleSave = async () => {
		setSaving(true)

		onSave?.(selectedBoardStage)

		setSaving(false)

		handleClose()
	}

	const handleSyncBoard = async () => {
		try {
			const boards = await onLoadBoardStages()

			if (boards) {
				setBoardOptions(boards)
			}
		} catch (error) {
			ErrorHandlerService.handle(error as Error)

			Notification.error({ message: "Houve um problema ao carregar os funis." })
		}
	}

	const clientAddedToFunnelEditorDialog = () => {
		return (
			<ActionDialog
				title="GATILHO POR ETAPA DO FUNIL"
				openDialog={openDialog}
				saveText={saveText || "FECHAR"}
				fullWidth={true}
				onSave={handleSave}
				onClose={handleClose}
				loading={saving}
				hideCloseButton={true}
				maxWidth="md"
			>
				<Grid container direction="column" spacing={2}>
					<Grid item xs={12}>
						<Typography variant="body1" color="textPrimary">
							Quando o contato for adicionado a <strong>etapa</strong> abaixo
						</Typography>
					</Grid>

					<Grid item xs={12}>
						<Grid container spacing={1} alignItems="center">
							<Grid item xs>
								<SelectInput
									defaultOption={trigger?.board?.stage}
									onChange={handleChange}
									options={boardOptions[0]?.stages || []}
									placeholder="Selecione a etapa do funil"
									getOptionSelected={(option, value) => option?.id === value?.id}
									noOptionsText="Sem opções"
									loadingText="Carregando..."
									onUpdate={handleSyncBoard}
								/>
							</Grid>
						</Grid>
					</Grid>

					{(boardOptions?.length === 0 || boardOptions[0]?.stages?.length === 0) && (
						<Grid item xs={12}>
							<Alert severity="warning" style={{ alignItems: "center" }}>
								<Grid container>
									<Typography>
										Não encontramos nenhum funil de vendas em sua conta. Crie seu funil de vendas {" "}
										<Link
											href={`${window.location.origin}/admin/funnel`}
											color="inherit"
											target="_blank"
										>
											aqui.
										</Link>
									</Typography>
								</Grid>
							</Alert>
						</Grid>
					)}
				</Grid>
			</ActionDialog>
		)
	}

	useDidMount(() => {
		handleSyncBoard()
	})

	return (
		<>
			<Grid
				onClick={handleOpenDialog}
			>
				{children}
			</Grid>
			{clientAddedToFunnelEditorDialog()}
		</>
	)
}

export default ClientAddedToFunnelEditorDialog
